import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './household-composition.reducer';

export const HouseholdComposition = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const householdCompositionList = useAppSelector(state => state.householdComposition.entities);
  const loading = useAppSelector(state => state.householdComposition.loading);
  const totalItems = useAppSelector(state => state.householdComposition.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="household-composition-heading" data-cy="HouseholdCompositionHeading">
        <Translate contentKey="apiLocationApp.householdComposition.home.title">Household Compositions</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.householdComposition.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/household-composition/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.householdComposition.home.createLabel">Create new Household Composition</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {householdCompositionList && householdCompositionList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.householdComposition.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.householdComposition.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.householdComposition.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.householdComposition.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.householdComposition.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllHouseholds')}>
                  <Translate contentKey="apiLocationApp.householdComposition.totalAllHouseholds">Total All Households</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllHouseholds')} />
                </th>
                <th className="hand" onClick={sort('onePersonHousehold')}>
                  <Translate contentKey="apiLocationApp.householdComposition.onePersonHousehold">One Person Household</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('onePersonHousehold')} />
                </th>
                <th className="hand" onClick={sort('singleFamilyHousehold')}>
                  <Translate contentKey="apiLocationApp.householdComposition.singleFamilyHousehold">Single Family Household</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('singleFamilyHousehold')} />
                </th>
                <th className="hand" onClick={sort('otherHouseholdTypes')}>
                  <Translate contentKey="apiLocationApp.householdComposition.otherHouseholdTypes">Other Household Types</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('otherHouseholdTypes')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {householdCompositionList.map((householdComposition, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/household-composition/${householdComposition.id}`} color="link" size="sm">
                      {householdComposition.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${householdComposition.geographicLevel}`} />
                  </td>
                  <td>{householdComposition.date}</td>
                  <td>{householdComposition.geography}</td>
                  <td>{householdComposition.geographyCode}</td>
                  <td>{householdComposition.totalAllHouseholds}</td>
                  <td>{householdComposition.onePersonHousehold}</td>
                  <td>{householdComposition.singleFamilyHousehold}</td>
                  <td>{householdComposition.otherHouseholdTypes}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/household-composition/${householdComposition.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/household-composition/${householdComposition.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/household-composition/${householdComposition.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.householdComposition.home.notFound">No Household Compositions found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={householdCompositionList && householdCompositionList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default HouseholdComposition;
